<template>
  <div class="space-y-6">
    <!-- Conditions -->
    <div>
      <h3 class="text-lg font-medium mb-3">Conditions ({{ filteredConditions.length }})</h3>
      <div class="space-y-3">
        <template v-for="option in filteredConditions" :key="option.value">
          <div
            v-if="option.count && option.count > 0"
            class="flex items-center py-2"
            @click="handleToggleFilterOption('condition', option.value)"
          >
            <Checkbox
              :id="`mobile-menu-condition-${option.value}`"
              :model-value="isFilterOptionSelected('condition', option.value)"
              @update:model-value="handleToggleFilterOption('condition', option.value)"
              class="mr-3 w-5 h-5"
            />
            <label
              :for="`mobile-menu-condition-${option.value}`"
              class="flex flex-1 cursor-pointer items-center justify-between text-base"
            >
              <span>{{ option.displayValue }}</span>
              <span class="text-muted-foreground text-sm">({{ option.count ?? 0 }})</span>
            </label>
          </div>
        </template>
      </div>
    </div>

    <!-- Body Types -->
    <div>
      <h3 class="text-lg font-medium mb-3">Body Types ({{ filteredBodyTypes.length }})</h3>
      <div class="space-y-3">
        <template v-for="option in filteredBodyTypes" :key="option.value">
          <div
            v-if="option.count && option.count > 0"
            class="flex items-center py-2"
            @click="handleToggleFilterOption('body', option.value)"
          >
            <Checkbox
              :id="`mobile-menu-body-${option.value}`"
              :model-value="isFilterOptionSelected('body', option.value)"
              @update:model-value="handleToggleFilterOption('body', option.value)"
              class="mr-3 w-5 h-5"
            />
            <label
              :for="`mobile-menu-body-${option.value}`"
              class="flex flex-1 cursor-pointer items-center justify-between text-base"
            >
              <span>{{ option.displayValue }}</span>
              <div class="flex items-center">
                <span 
                  v-for="(condCount, condName) in option.conditionCounts"
                  :key="condName"
                  v-show="condCount > 0"
                  class="text-xs px-1.5 py-0.5 rounded-full ml-1 capitalize"
                  :class="{
                    'bg-blue-100 text-blue-700': condName === 'new',
                    'bg-green-100 text-green-700': condName === 'used',
                    'bg-yellow-100 text-yellow-700': condName === 'demo',
                    'bg-gray-100 text-gray-700': !['new', 'used', 'demo'].includes(String(condName))
                  }"
                >
                  {{ condName }}: {{ condCount }}
                </span>
                <span class="text-muted-foreground text-sm ml-2">({{ option.count ?? 0 }})</span>
              </div>
            </label>
          </div>
        </template>
      </div>
    </div>

    <!-- Makes -->
    <div>
      <h3 class="text-lg font-medium mb-3">Makes ({{ filteredMakes.length }})</h3>
      <div class="space-y-3">
        <template v-for="option in filteredMakes" :key="option.value">
          <div
            v-if="option.count && option.count > 0"
            class="flex items-center py-2"
            @click="handleToggleFilterOption('make', option.value)"
          >
            <Checkbox
              :id="`mobile-menu-make-${option.value}`"
              :model-value="isFilterOptionSelected('make', option.value)"
              @update:model-value="handleToggleFilterOption('make', option.value)"
              class="mr-3 w-5 h-5"
            />
            <label
              :for="`mobile-menu-make-${option.value}`"
              class="flex flex-1 cursor-pointer items-center justify-between text-base"
            >
              <span>{{ option.displayValue }}</span>
              <div class="flex items-center">
                <span 
                  v-for="(condCount, condName) in option.conditionCounts"
                  :key="condName"
                  v-show="condCount > 0"
                  class="text-xs px-1.5 py-0.5 rounded-full ml-1 capitalize"
                  :class="{
                    'bg-blue-100 text-blue-700': condName === 'new',
                    'bg-green-100 text-green-700': condName === 'used',
                    'bg-yellow-100 text-yellow-700': condName === 'demo',
                    'bg-gray-100 text-gray-700': !['new', 'used', 'demo'].includes(String(condName))
                  }"
                >
                  {{ condName }}: {{ condCount }}
                </span>
                <span class="text-muted-foreground text-sm ml-2">({{ option.count ?? 0 }})</span>
              </div>
            </label>
          </div>
        </template>
      </div>
    </div>

    <!-- Models -->
    <div>
      <h3 class="text-lg font-medium mb-3">Models ({{ filteredModels.length }})</h3>

      <!-- Search input for models in mobile menu -->
      <div class="mb-4 relative">
        <input
          v-model="modelSearchQuery"
          type="text"
          placeholder="Search models..."
          class="w-full px-3 py-2 pr-12 text-sm rounded-md border border-input bg-background text-foreground focus:outline-hidden focus:ring-2 focus:ring-primary focus:border-primary"
        />
        <div class="absolute inset-y-0 right-0 flex items-center">
          <button
            v-if="modelSearchQuery"
            @click="clearModelSearch"
            class="px-2 text-muted-foreground hover:text-foreground"
            aria-label="Clear model search"
          >
            <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
              <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clip-rule="evenodd" />
            </svg>
          </button>
          <button
            class="px-2 text-muted-foreground hover:text-foreground"
            aria-label="Search models"
          >
            <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
              <path fill-rule="evenodd" d="M8 4a4 4 0 100 8 4 4 0 000-8zM2 8a6 6 0 1110.89 3.476l4.817 4.817a1 1 0 01-1.414 1.414l-4.816-4.816A6 6 0 012 8z" clip-rule="evenodd" />
            </svg>
          </button>
        </div>
      </div>

      <div class="space-y-3">
        <template v-for="option in filteredModels" :key="option.value">
          <div
            v-if="option.count && option.count > 0"
            class="flex items-center py-2"
            @click="handleToggleFilterOption('model', option.value)"
          >
            <Checkbox
              :id="`mobile-menu-model-${option.value}`"
              :model-value="isFilterOptionSelected('model', option.value)"
              @update:model-value="handleToggleFilterOption('model', option.value)"
              class="mr-3 w-5 h-5"
            />
            <label
              :for="`mobile-menu-model-${option.value}`"
              class="flex flex-1 cursor-pointer items-center justify-between text-base"
            >
              <span>{{ option.displayMake }} {{ option.displayValue }}</span>
              <div class="flex items-center">
                <span 
                  v-for="(condCount, condName) in option.conditionCounts"
                  :key="condName"
                  v-show="condCount > 0"
                  class="text-xs px-1.5 py-0.5 rounded-full ml-1 capitalize"
                  :class="{
                    'bg-blue-100 text-blue-700': condName === 'new',
                    'bg-green-100 text-green-700': condName === 'used',
                    'bg-yellow-100 text-yellow-700': condName === 'demo',
                    'bg-gray-100 text-gray-700': !['new', 'used', 'demo'].includes(String(condName))
                  }"
                >
                  {{ condName }}: {{ condCount }}
                </span>
                <span class="text-muted-foreground text-sm ml-2">({{ option.count ?? 0 }})</span>
              </div>
            </label>
          </div>
        </template>
      </div>
    </div>

    <!-- Weekly Budget -->
    <div>
      <h3 class="text-lg font-medium mb-3">Weekly Budget</h3>
      <div class="flex justify-between items-center mb-2 text-sm">
        <span>${{ weeklyBudget[0] }}</span>
        <span>${{ weeklyBudget[1] }}</span>
      </div>
      <ClientOnly>
        <template #default>
          <Slider
            :min="minWeeklyBudget"
            :max="maxWeeklyBudget"
            :model-value="weeklyBudget"
            :step="1"
            @update:model-value="updateBudgetFilter"
            class="mb-4"
          />
        </template>
        <template #fallback>
          <div class="h-2 bg-muted rounded-full mb-4"></div>
        </template>
      </ClientOnly>
    </div>

    <!-- Apply Filters Button -->
    <div class="px-4 border-t border-border bg-primary rounded-lg">
      <NuxtLink
        :to="{ path: '/cars-for-sale', query: currentFilterQuery }"
        class="bg-primary hover:bg-primary/90 transition-colors duration-300 rounded-lg overflow-hidden flex items-center justify-center text-primary-foreground font-medium p-4 w-full text-base"
        @click="$emit('close-menu')"
      >
        <span class="mr-2">»</span> Show {{ filteredVehicleCount }} cars
      </NuxtLink>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue'
import { useVehiclesStore } from '~/stores/vehicles'
import { useFrontPageStore, type FrontPageFilters } from '~/stores/frontPage'
import { useFilteredOptions } from '~/layers/base/composables/useFilteredOptions'
import { Checkbox } from '~/layers/base/components/ui/checkbox'
import { Slider } from '~/layers/base/components/ui/slider'

const emit = defineEmits<{
  (e: 'close-menu'): void
}>()

const vehiclesStore = useVehiclesStore()
const frontPageStore = useFrontPageStore()

// Use the same composable as FacetedSearchWidget for consistent data
const {
  modelSearchQuery,
  filteredConditions,
  filteredMakes,
  filteredModels,
  filteredBodyTypes,
  clearModelSearch
} = useFilteredOptions()

// Budget data from vehicles store
const weeklyBudget = computed(() => {
  const budget = frontPageStore.frontPageFilters.perweek
  return budget ? [budget.min, budget.max] : [0, 1000]
})

const minWeeklyBudget = computed(() => vehiclesStore.availableFilters.perweekRange?.min ?? 0)
const maxWeeklyBudget = computed(() => vehiclesStore.availableFilters.perweekRange?.max ?? 1000)
const filteredVehicleCount = computed(() => frontPageStore.navSearchFilteredCount)
const currentFilterQuery = computed(() => frontPageStore.currentFilterQuery)

// Filter methods using frontPageStore (same implementation as FacetedSearchWidget)
type FrontPageArrayFilterKey = 'condition' | 'make' | 'model' | 'body'

const isFilterOptionSelected = (filterName: string, optionValue: string) => {
  const selected = frontPageStore.frontPageFilters[filterName as keyof typeof frontPageStore.frontPageFilters]
  const isSelected = Array.isArray(selected) && selected.includes(optionValue)
  return isSelected
}

const handleToggleFilterOption = (filterName: string, optionValue: string) => {
  const typedFilterName = filterName as FrontPageArrayFilterKey
  
  // Ensure filterName is a valid key before proceeding
  if (!(typedFilterName in frontPageStore.frontPageFilters)) return
  
  // Safely get current values, defaulting to an empty array
  const currentValues = [...(frontPageStore.frontPageFilters[typedFilterName] || [])]
  const index = currentValues.indexOf(optionValue)
  if (index > -1) {
    currentValues.splice(index, 1) // Remove
  } else {
    currentValues.push(optionValue) // Add
  }
  frontPageStore.updateFilter(typedFilterName, currentValues)
}

const updateBudgetFilter = (value: number[] | undefined) => {
  if (value && value.length === 2) {
    frontPageStore.updateBudgetFilter({ min: value[0], max: value[1] })
  }
}
</script>